import {
  useState, memo, useCallback, useMemo, SyntheticEvent,
} from 'react';
import {
  Button,
  cardNumberValidator,
  cardCvvValidator,
  cardExpirationDateFormatValidator,
  ClosedEyeIcon,
  CreditCard,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  EyeIcon,
  Grid,
  IconButton,
  useTranslations,
  parseCardExpirationDate,
  ExpiryDateTextField,
  NumericTextField,
  getCardType,
  maskCVV,
  emptyStringValidator,
  ReactHookFormTextField,
  IExpiryDateTextFieldProps,
  INumericTextFieldProps,
  TTextFieldProps,
} from '@uniqkey-frontend/shared-app';
import { useForm } from 'react-hook-form';

export interface ICreateCreditCardReturnValue {
  name: string;
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  cardHolderName: string;
}

interface ICreateCreditCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (params: ICreateCreditCardReturnValue) => Promise<void>;
  isLoading: boolean;
}

const CreateCreditCardModal = (props: ICreateCreditCardModalProps) => {
  const {
    isOpen, onClose, onSubmit, isLoading,
  } = props;
  const [isFlip, setIsFlip] = useState<boolean>(false);
  const [isCvvVisible, setIsCvvVisible] = useState<boolean>(false);
  const { t } = useTranslations();
  const {
    register, setValue, watch, handleSubmit, formState: { errors, dirtyFields }, control,
  } = useForm<ICreateCreditCardReturnValue>({
    mode: 'all',
    defaultValues: {
      name: '',
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardHolderName: '',
    },
  });

  const [
    cardNumber, cardHolder, expiryDate, cvv,
  ] = watch(['cardNumber', 'cardHolderName', 'expiryDate', 'cvv']);
  const { month: expirationMonth, year: expirationYear } = useMemo(
    () => parseCardExpirationDate(expiryDate),
    [expiryDate],
  );

  const handleFlip = useCallback(() => setIsFlip((prevState) => !prevState), []);

  const handleExpiryDateChange = useCallback<
    NonNullable<IExpiryDateTextFieldProps['onChange']>
  >((event) => {
    setValue('expiryDate', event.target.value);
    setIsFlip(false);
  }, [setValue]);

  const handleCardHolderNameChange = useCallback<
    NonNullable<TTextFieldProps['onChange']>
  >((event) => {
    setValue('cardHolderName', event.target.value);
    setIsFlip(false);
  }, [setValue]);

  const handleCvvChange = useCallback<
    NonNullable<INumericTextFieldProps['onChange']>
  >((event) => {
    setValue('cvv', event.target.value);
    setIsFlip(true);
  }, [setValue]);

  const handleCvvBlur = useCallback(() => {
    setIsFlip(false);
  }, []);

  const toggleCvvVisibility = useCallback((event: SyntheticEvent) => {
    event.preventDefault();
    setIsCvvVisible((prevState) => !prevState);
  }, []);

  const handleCardNumberChange = useCallback<
    NonNullable<INumericTextFieldProps['onChange']>
  >((event) => {
    setValue('cardNumber', event.target.value);
    setIsFlip(false);
  }, [setValue]);

  const cardNumberType = useMemo(() => getCardType(cardNumber), [cardNumber]);
  const maskedCVV = useMemo(() => maskCVV(cvv), [cvv]);

  const isSubmitButtonDisabled = useMemo(
    () => !dirtyFields.name || !dirtyFields.cardNumber,
    [dirtyFields.name, dirtyFields.cardNumber],
  );

  return (
    <Dialog
      onClose={onClose}
      open={isOpen}
      fullWidth
      maxWidth="sm"
      clickOutsideToClose={!isLoading}
    >
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <DialogTitle isLoading={isLoading} onClose={onClose}>
          {t('createCreditCardModal.title')}
        </DialogTitle>
        <DialogContent>
          <Grid container direction="column" rowSpacing={4}>
            <Grid item xs={12} alignSelf="center">
              <CreditCard
                flip={isFlip}
                onClick={handleFlip}
                cardNumber={cardNumber}
                cardHolder={cardHolder}
                cardNumberType={cardNumberType}
                expirationMonth={expirationMonth}
                expirationYear={expirationYear}
                cvv={isCvvVisible ? cvv : maskedCVV}
                monthTranslationKey={t('common.month')}
                yearTranslationKey={t('common.year')}
              />
            </Grid>
            <Grid item xs={12}>
              <ReactHookFormTextField
                name="name"
                control={control}
                autoFocus
                fullWidth
                error={!!errors.name}
                required
                label={t('createCreditCardModal.name.label')}
                placeholder={t('createCreditCardModal.name.placeholder')}
                helperText={errors.name?.message}
                rules={{
                  required: t('validation.required'),
                  validate: (value) => (
                    emptyStringValidator(value) ? t('validation.required') : true
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <ReactHookFormTextField
                name="cardHolderName"
                control={control}
                fullWidth
                error={!!errors.cardHolderName}
                label={t('createCreditCardModal.cardHolderName.label')}
                placeholder={t('createCreditCardModal.cardHolderName.placeholder')}
                helperText={errors.cardHolderName?.message}
                value={cardHolder}
                onChange={handleCardHolderNameChange}
              />
            </Grid>
            <Grid item xs={12}>
              <NumericTextField
                decimalSeparator=""
                allowNegative={false}
                fullWidth
                error={!!errors.cardNumber}
                required
                label={t('createCreditCardModal.cardNumber.label')}
                placeholder={t('createCreditCardModal.cardNumber.placeholder')}
                helperText={errors.cardNumber?.message}
                value={cardNumber}
                onChange={handleCardNumberChange}
                inputProps={{
                  maxLength: 19,
                  ...register('cardNumber', {
                    required: t('validation.required'),
                    validate: (value) => (
                      cardNumberValidator(value) ? true : t('validation.invalidCreditCardNumber')
                    ),
                  }),
                }}
              />
            </Grid>
            <Grid item container columnSpacing={2}>
              <Grid item xs={6}>
                <ExpiryDateTextField
                  fullWidth
                  error={!!errors.expiryDate}
                  label={t('createCreditCardModal.expiryDate.label')}
                  placeholder={t('createCreditCardModal.expiryDate.placeholder')}
                  helperText={errors.expiryDate?.message}
                  value={expiryDate}
                  onChange={handleExpiryDateChange}
                  inputProps={{
                    ...register('expiryDate', {
                      validate: (value) => {
                        if (!value) return true;
                        const { isValid } = cardExpirationDateFormatValidator(value);
                        return isValid ? true : t('validation.invalidFormat');
                      },
                    }),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <NumericTextField
                  decimalSeparator=""
                  allowNegative={false}
                  fullWidth
                  type={isCvvVisible ? 'text' : 'password'}
                  error={!!errors.cvv}
                  label={t('createCreditCardModal.cvv.label')}
                  placeholder={t('createCreditCardModal.cvv.placeholder')}
                  helperText={errors.cvv?.message}
                  value={cvv}
                  onChange={handleCvvChange}
                  inputProps={{
                    ...register('cvv', {
                      validate: (value) => {
                        if (!value) return true;
                        return cardCvvValidator(value)
                          ? true : t('validation.invalidFormat');
                      },
                    }),
                    maxLength: 4,
                    onBlur: handleCvvBlur,
                  }}
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                  InputProps={{
                    endAdornment: (
                      <IconButton onMouseDown={toggleCvvVisibility}>
                        {isCvvVisible ? <ClosedEyeIcon /> : <EyeIcon />}
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid item container spacing={2}>
            <Grid item xs={6}>
              <Button
                isLoading={isLoading}
                disabled={isSubmitButtonDisabled}
                fullWidth
                type="submit"
              >
                {t('common.create')}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                disabled={isLoading}
                fullWidth
                onClick={onClose}
              >
                {t('common.cancel')}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default memo(CreateCreditCardModal);
